import { Theme } from '@emotion/react';
import { SxProps, Typography, styled, Container } from '@mui/material';
import { memo, ReactElement, useMemo } from 'react';
import { ApiError } from './ApiLoadingGuard';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTranslation } from 'react-i18next';

export interface DefaultErrorScreenProps {
  errors?: ApiError[];
  defaultText?: string;
  textOverride?: { status: number; text: string }[];
  iconSize?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
}

/**
 * @Note: Provided sx styles are merged with the default styles.
 */
export const DefaultErrorScreen = memo(function ({
  errors = [],
  defaultText = 'error.default',
  textOverride = [],
  iconSize = 'large',
  sx,
}: DefaultErrorScreenProps): ReactElement {
  const { t } = useTranslation();
  const messageToDisplay = useErrorMessage(errors, textOverride, defaultText);
  return (
    <StyledContainer sx={sx}>
      <ErrorOutlineIcon fontSize={iconSize} />
      <Typography variant='h6' textAlign={'center'}>
        {t(messageToDisplay)}
      </Typography>
    </StyledContainer>
  );
});

const StyledContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Custom hook to derive the error message to be displayed.
 * It checks for overridden messages based on the error status and
 * the provided textOverride array. If no overrides match, it falls back
 * to the default text.
 */
function useErrorMessage(
  errors: ApiError[] = [],
  textOverride: { status: number; text: string }[] = [],
  defaultText: string
): string {
  return useMemo(() => {
    if (textOverride.length > 0 && errors.length > 0) {
      const overriddenMessages = textOverride
        .map((overrideRule) => {
          const matchedError = errors.find((err) => err.statusCode === overrideRule.status);
          if (matchedError) {
            // Use the override text; if it's empty, fallback to the error's message
            return overrideRule.text || matchedError.message;
          }
          return null; // No error matched this specific override rule
        })
        .filter(Boolean) as string[];

      if (overriddenMessages.length > 0) {
        return overriddenMessages.join(', ');
      }
    }

    return defaultText;
  }, [errors, textOverride, defaultText]);
}
